import { Loader2 } from 'lucide-react';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'circular' | 'dots' | 'pulse';

export interface SpinnerProps {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Color class for the spinner (Tailwind color class) */
  color?: string;
  /** Show as full page overlay */
  fullPage?: boolean;
  /** Accessible label for screen readers */
  label?: string;
  /** Variant of the spinner animation */
  variant?: SpinnerVariant;
  /** Additional class names */
  className?: string;
}

const sizeClasses: Record<SpinnerSize, { icon: string; text: string; dots: string }> = {
  xs: { icon: 'w-3 h-3', text: 'text-xs', dots: 'w-1.5 h-1.5' },
  sm: { icon: 'w-4 h-4', text: 'text-sm', dots: 'w-2 h-2' },
  md: { icon: 'w-6 h-6', text: 'text-base', dots: 'w-2.5 h-2.5' },
  lg: { icon: 'w-8 h-8', text: 'text-lg', dots: 'w-3 h-3' },
  xl: { icon: 'w-12 h-12', text: 'text-xl', dots: 'w-4 h-4' },
};

/** Circular spinning animation (default) */
function CircularSpinner({ size = 'md', color = 'text-primary-600', className = '' }: Omit<SpinnerProps, 'variant' | 'fullPage' | 'label'>) {
  return (
    <Loader2
      className={`animate-spin ${sizeClasses[size].icon} ${color} ${className}`}
      aria-hidden="true"
    />
  );
}

/** Dots bouncing animation */
function DotsSpinner({ size = 'md', color = 'bg-primary-600', className = '' }: Omit<SpinnerProps, 'variant' | 'fullPage' | 'label'>) {
  const dotSize = sizeClasses[size].dots;

  return (
    <div className={`flex items-center gap-1 ${className}`} aria-hidden="true">
      <span
        className={`${dotSize} ${color} rounded-full animate-bounce`}
        style={{ animationDelay: '0ms' }}
      />
      <span
        className={`${dotSize} ${color} rounded-full animate-bounce`}
        style={{ animationDelay: '150ms' }}
      />
      <span
        className={`${dotSize} ${color} rounded-full animate-bounce`}
        style={{ animationDelay: '300ms' }}
      />
    </div>
  );
}

/** Pulse animation */
function PulseSpinner({ size = 'md', color = 'bg-primary-600', className = '' }: Omit<SpinnerProps, 'variant' | 'fullPage' | 'label'>) {
  const iconSize = sizeClasses[size].icon;

  return (
    <span
      className={`${iconSize} ${color} rounded-full animate-pulse ${className}`}
      aria-hidden="true"
    />
  );
}

/**
 * Spinner component for loading states
 *
 * @example
 * // Basic usage
 * <Spinner />
 *
 * @example
 * // With label
 * <Spinner label="Loading data..." />
 *
 * @example
 * // Full page overlay
 * <Spinner fullPage label="Please wait..." />
 *
 * @example
 * // Dots variant
 * <Spinner variant="dots" size="lg" />
 */
export function Spinner({
  size = 'md',
  color,
  fullPage = false,
  label,
  variant = 'circular',
  className = '',
}: SpinnerProps) {
  // Determine color based on variant
  const spinnerColor = color || (variant === 'circular' ? 'text-primary-600' : 'bg-primary-600');

  // Render the appropriate spinner variant
  const renderSpinner = () => {
    switch (variant) {
      case 'dots':
        return <DotsSpinner size={size} color={spinnerColor} className={className} />;
      case 'pulse':
        return <PulseSpinner size={size} color={spinnerColor} className={className} />;
      case 'circular':
      default:
        return <CircularSpinner size={size} color={spinnerColor} className={className} />;
    }
  };

  // Content with optional label
  const content = (
    <div
      className="flex flex-col items-center justify-center gap-2"
      role="status"
      aria-live="polite"
    >
      {renderSpinner()}
      {label && (
        <span className={`${sizeClasses[size].text} text-slate-600 dark:text-slate-400`}>
          {label}
        </span>
      )}
      <span className="sr-only">{label || 'Loading...'}</span>
    </div>
  );

  // Full page overlay
  if (fullPage) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-slate-950/80 backdrop-blur-xs">
        {content}
      </div>
    );
  }

  return content;
}

/**
 * Inline spinner for use within buttons or text
 *
 * @example
 * <button disabled={loading}>
 *   {loading && <InlineSpinner />}
 *   {loading ? 'Saving...' : 'Save'}
 * </button>
 */
export function InlineSpinner({
  size = 'sm',
  color = 'currentColor',
  className = ''
}: Pick<SpinnerProps, 'size' | 'color' | 'className'>) {
  return (
    <Loader2
      className={`animate-spin ${sizeClasses[size].icon} ${className}`}
      style={{ color }}
      aria-hidden="true"
    />
  );
}

/**
 * Button with built-in loading state
 *
 * @example
 * <LoadingButton loading={isSubmitting} onClick={handleSubmit}>
 *   Save Changes
 * </LoadingButton>
 */
export interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingText?: string;
  spinnerSize?: SpinnerSize;
  variant?: 'primary' | 'secondary' | 'danger';
  children: React.ReactNode;
}

export function LoadingButton({
  loading = false,
  loadingText,
  spinnerSize = 'sm',
  variant = 'primary',
  children,
  disabled,
  className = '',
  ...props
}: LoadingButtonProps) {
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white disabled:bg-primary-400',
    secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 disabled:bg-slate-50 dark:disabled:bg-slate-900',
    danger: 'bg-red-600 hover:bg-red-700 text-white disabled:bg-red-400',
  };

  return (
    <button
      disabled={disabled || loading}
      className={`
        inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium
        transition-colors duration-200 disabled:cursor-not-allowed
        ${variantClasses[variant]}
        ${className}
      `}
      {...props}
    >
      {loading && <InlineSpinner size={spinnerSize} color="currentColor" />}
      {loading && loadingText ? loadingText : children}
    </button>
  );
}

/**
 * Page loading skeleton with spinner
 *
 * @example
 * if (loading) return <PageLoader label="Loading dashboard..." />;
 */
export function PageLoader({
  label = 'Loading...',
  size = 'lg'
}: Pick<SpinnerProps, 'label' | 'size'>) {
  return (
    <div className="flex items-center justify-center min-h-[400px] w-full">
      <Spinner size={size} label={label} />
    </div>
  );
}

export default Spinner;
